import { format, isValid, parse } from 'date-fns';

import type { HwcgIncidentSpecificsState } from './hwcgIncidentSpecifics';

export const HWCG_EOC_CHANNELS = ['inApp', 'email', 'sms', 'voice'] as const;

export type HwcgEocChannel = (typeof HWCG_EOC_CHANNELS)[number];

export type HwcgEocPoint = {
  type: 'Point';
  coordinates: [number, number];
};

export type HwcgEocNoticeState = {
  locationLabel: string;
  location: HwcgEocPoint | null;
  recipientContactIds: string[];
  /** Null keeps the generated script, so later intake edits still fill it in. */
  confidentialityScript: string | null;
  activateScript: string | null;
  notifyScript: string | null;
  /** Responsible Party activation: when people should arrive. */
  reportToTime: string;
  channels: HwcgEocChannel[];
  /**
   * Roster activation request text per channel; a missing key sends the default
   * script.
   */
  activationMessages?: Partial<HwcgActivationMessages>;
};

export type HwcgActivationMessages = {
  emailSubject: string;
  emailMessage: string;
  smsMessage: string;
  appMessage: string;
  phoneMessage: string;
};

export const HWCG_ACTIVATION_MESSAGE_KEY: Record<
  HwcgEocChannel,
  Exclude<keyof HwcgActivationMessages, 'emailSubject'>
> = {
  inApp: 'appMessage',
  email: 'emailMessage',
  sms: 'smsMessage',
  voice: 'phoneMessage',
};

/** Matches the server's opening line on a generated activation request. */
export const HWCG_ACTIVATION_CONFIDENTIALITY_LINE =
  'This is a confidential HWCG communication. Keep its contents confidential.';

export const HWCG_ROSTER_REPORT_REPLY =
  'Please respond 1 when you have reported. Respond 9 if you cannot report.';

/**
 * What a channel sends when it has no message of its own, mirroring the
 * server's default.
 */
export function hwcgActivationDefaultMessage(
  activateScript: string | null,
  template: string
): string {
  const saved = activateScript?.trim();
  if (saved) return saved;
  return `${HWCG_ACTIVATION_CONFIDENTIALITY_LINE} ${template.trim()} ${HWCG_ROSTER_REPORT_REPLY}`;
}

export const HWCG_EOC_CONFIDENTIALITY_SCRIPT =
  'This is a confidential HWCG communication. By continuing, you agree to keep the contents of this notification confidential. If you agree, press 1 on your phone, or reply 1 by text.';

/** Shown on the EOC notice and sent in place of the confidentiality step. */
export const HWCG_EOC_LOGIN_AGREEMENT =
  'By logging in to PRATUS, you agree to keep the contents of this notification confidential.';

/**
 * Older drafts stored this where the Group Actions link belongs. It is removed
 * from the editor. The server inserts the real URL when the notice is sent.
 */
export const HWCG_GROUP_ACTIONS_LINK_TOKEN = '{{HWCG_GROUP_ACTIONS_URL}}';

export function stripHwcgGroupActionsLinkToken(script: string): string {
  return script
    .split(HWCG_GROUP_ACTIONS_LINK_TOKEN)
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const HWCG_GROUP_REPLY_PROMPTS = [
  'Please respond 1 to confirm receipt of this message.',
  'Please respond 1 when you have reported. Respond 9 if you cannot report.',
  'Please respond with 1 to acknowledge receipt of this message.',
] as const;

/**
 * Drops numeric reply lines from a Group Actions script, including one saved
 * before in-app acknowledgement.
 */
export function stripHwcgGroupReplyPrompts(script: string): string {
  let next = script;
  for (const prompt of HWCG_GROUP_REPLY_PROMPTS)
    next = next.split(prompt).join('');
  return next
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function withHwcgLoginAgreement(script: string): string {
  const body = script.trim();
  if (body.includes(HWCG_EOC_LOGIN_AGREEMENT)) return body;
  return `${body}\n\n${HWCG_EOC_LOGIN_AGREEMENT}`;
}

type EocScriptSpecifics = Pick<
  HwcgIncidentSpecificsState,
  'time' | 'date' | 'companyName' | 'wellName' | 'blockNumber' | 'waterDepthFt'
>;

function filled(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim() ?? '';
  return trimmed || fallback;
}

function depthPhrase(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return 'an unknown water depth';
  return /ft\b/i.test(trimmed) ? trimmed : `${trimmed} ft`;
}

export function hwcgEocActivateScript(
  specifics: EocScriptSpecifics,
  reportToLocation = '',
  reportToTime = ''
): string {
  const when = `${filled(specifics.time, 'an unknown time')} on ${filled(specifics.date, 'an unknown date')}`;
  const place =
    reportToLocation.trim() ||
    filled(specifics.companyName, 'the responsible party office');
  const arrive =
    reportToTime.trim() || filled(specifics.time, 'an unknown time');
  return withHwcgLoginAgreement(
    `This is an HWCG Activation Notice. At ${when}, ${filled(specifics.companyName, 'a responsible party')} reported an incident at ${filled(specifics.wellName, 'an unnamed well')} and ${filled(specifics.blockNumber, 'an unnamed block')}, water depth ${depthPhrase(specifics.waterDepthFt)}. Please report to ${place} at ${arrive}. Log into PRATUS to accept or decline the activation request.`
  );
}

export function hwcgResponsiblePartyActivateScript(
  specifics: EocScriptSpecifics,
  reportToLocation: string,
  reportToTime: string
): string {
  const when = `${filled(specifics.time, 'an unknown time')} on ${filled(specifics.date, 'an unknown date')}`;
  const place =
    reportToLocation.trim() ||
    filled(specifics.companyName, 'the responsible party office');
  const arrive =
    reportToTime.trim() || filled(specifics.time, 'an unknown time');
  return withHwcgLoginAgreement(
    `This is an HWCG Activation Notice. At ${when}, ${filled(specifics.companyName, 'a responsible party')} reported an incident at ${filled(specifics.wellName, 'an unnamed well')} and ${filled(specifics.blockNumber, 'an unnamed block')}, water depth ${depthPhrase(specifics.waterDepthFt)}. Please report to ${place} at ${arrive}. Log into PRATUS to accept or decline the activation request.`
  );
}

/** Confidentiality step for roster activation outside HWCG. */
export const ROSTER_CONFIDENTIALITY_SCRIPT =
  'This is a confidential incident communication. By continuing, you agree to keep its contents confidential. If you agree, press 1 on your phone, or reply 1 by text.';

/** Roster activation script outside HWCG, where there is no well intake. */
export function rosterActivateScript(
  incidentName: string,
  reportToLocation: string,
  reportToTime: string
): string {
  const incident = filled(incidentName, 'a new incident');
  const place = filled(reportToLocation, 'your assigned location');
  const arrive = filled(reportToTime, 'the time you are given');
  return `You have been activated for ${incident}. Please report to ${place} at ${arrive}. Log into PRATUS to accept or decline the activation request.`;
}

export function hwcgEocNotifyScript(
  specifics: Pick<HwcgIncidentSpecificsState, 'time' | 'date'>
): string {
  const when = `${filled(specifics.time, 'an unknown time')} on ${filled(specifics.date, 'an unknown date')}`;
  return withHwcgLoginAgreement(
    `This is an HWCG Activation Notice. At ${when}, a HWCG member reported an incident. No action is required at this time. Acknowledge this notification in PRATUS.`
  );
}

export function createEmptyHwcgEocNotice(): HwcgEocNoticeState {
  return {
    locationLabel: '',
    location: null,
    recipientContactIds: [],
    confidentialityScript: null,
    activateScript: null,
    notifyScript: null,
    reportToTime: '',
    // Include SMS by default: the confidentiality script asks recipients to reply 1 by text.
    channels: ['inApp', 'email', 'sms', 'voice'],
    activationMessages: {},
  };
}

export function hwcgActivateScriptFor(
  groupId: 'eoc' | 'responsible_party',
  notice: HwcgEocNoticeState,
  specifics: EocScriptSpecifics
): string {
  if (notice.activateScript) {
    return stripHwcgGroupReplyPrompts(
      stripHwcgGroupActionsLinkToken(notice.activateScript)
    );
  }
  if (groupId === 'responsible_party') {
    return hwcgResponsiblePartyActivateScript(
      specifics,
      notice.locationLabel,
      notice.reportToTime
    );
  }
  return hwcgEocActivateScript(
    specifics,
    notice.locationLabel,
    notice.reportToTime
  );
}

export function hwcgEocCompanyMatches(
  memberCompany: string | null | undefined,
  responsibleParty: string
): boolean {
  const company = responsibleParty.trim().toLowerCase();
  if (!company) return false;
  return (memberCompany ?? '').trim().toLowerCase() === company;
}

export function hwcgGroupNoticePayload(
  groupId: 'eoc' | 'responsible_party' | 'mutual_aid' | 'service_provider',
  action: 'activate' | 'notify',
  notice: HwcgEocNoticeState,
  specifics: EocScriptSpecifics &
    Pick<HwcgIncidentSpecificsState, 'time' | 'date'>
) {
  const activateGroup =
    groupId === 'responsible_party' ? 'responsible_party' : 'eoc';
  return {
    action,
    recipientContactIds: notice.recipientContactIds,
    confidentialityScript:
      notice.confidentialityScript ?? HWCG_EOC_CONFIDENTIALITY_SCRIPT,
    noticeScript: stripHwcgGroupReplyPrompts(
      action === 'activate'
        ? hwcgActivateScriptFor(activateGroup, notice, specifics)
        : (notice.notifyScript ?? hwcgEocNotifyScript(specifics))
    ),
    channels: notice.channels,
  };
}

export function hwcgEocCreatePayload(
  action: 'activate' | 'notify',
  notice: HwcgEocNoticeState,
  specifics: EocScriptSpecifics &
    Pick<HwcgIncidentSpecificsState, 'time' | 'date'>
) {
  return {
    action,
    recipientContactIds: notice.recipientContactIds,
    confidentialityScript:
      notice.confidentialityScript ?? HWCG_EOC_CONFIDENTIALITY_SCRIPT,
    noticeScript: stripHwcgGroupReplyPrompts(
      action === 'activate'
        ? hwcgActivateScriptFor('eoc', notice, specifics)
        : (notice.notifyScript ?? hwcgEocNotifyScript(specifics))
    ),
    channels: notice.channels,
  };
}

export function hwcgEocRecipients<
  T extends { id: string; memberCompany?: string | null },
>(
  contacts: T[],
  responsibleParty: string,
  selectedIds?: readonly string[]
): T[] {
  const selected = selectedIds ? new Set(selectedIds) : null;
  const seen = new Set<string>();
  const matches: T[] = [];
  for (const contact of contacts) {
    if (seen.has(contact.id)) continue;
    if (!hwcgEocCompanyMatches(contact.memberCompany, responsibleParty))
      continue;
    if (selected && !selected.has(contact.id)) continue;
    seen.add(contact.id);
    matches.push(contact);
  }
  return matches;
}

/**
 * How a picked report-to time reads in the activation script: "05:30 PM on
 * 10/29/2026".
 */
const REPORT_TO_TIME_FORMAT = "hh:mm a 'on' MM/dd/yyyy";
/** Times typed before the picker existed, e.g. "1200 10/29/26". */
const LEGACY_REPORT_TO_TIME_FORMAT = 'HHmm MM/dd/yy';

export function formatHwcgReportToTime(date: Date): string {
  return format(date, REPORT_TO_TIME_FORMAT);
}

/**
 * The picker's date and time for a saved report-to time; null when it was free
 * text.
 */
export function parseHwcgReportToTime(text: string): Date | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  for (const pattern of [REPORT_TO_TIME_FORMAT, LEGACY_REPORT_TO_TIME_FORMAT]) {
    const parsed = parse(trimmed, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }
  return null;
}
